import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import { ActionItem, User, Rock, Issue, Metric, MetricName, COLLECTION_NAMES } from '../types';

/**
 * Hook for managing Action Items data with Firestore
 * Unified hook that supports both general action items (userId) and rock-specific (rockId)
 * Replaces Supabase queries with Firestore v9+ modular API
 */
export function useActionItems(userId?: string | undefined, rockId?: string | undefined) {
  const [actionItems, setActionItems] = useState<ActionItem[]>([]);
  const { eosUsers = [] } = useAuth();
  const [rocks, setRocks] = useState<Rock[]>([]);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [rawMetrics, setRawMetrics] = useState<Metric[]>([]);
  const [metricNames, setMetricNames] = useState<MetricName[]>([]);
  const [loading, setLoading] = useState(true);

  // Load action items with real-time listener
  useEffect(() => {
    if (rockId) {
      // Rock-specific: query both rockId and linkedRockId
      const rockSpecificQuery = query(
        collection(db, COLLECTION_NAMES.ACTION_ITEMS),
        where('rockId', '==', rockId),
        where('dropped', '==', false),
        orderBy('createdAt', 'desc')
      );
      const linkedQuery = query(
        collection(db, COLLECTION_NAMES.ACTION_ITEMS),
        where('linkedRockId', '==', rockId),
        where('dropped', '==', false),
        orderBy('createdAt', 'desc')
      );

      let rockSpecificData: ActionItem[] = [];
      let linkedData: ActionItem[] = [];

      // Set up listeners for both queries
      const unsubRockSpecific = onSnapshot(
        rockSpecificQuery,
        (snapshot) => {
          rockSpecificData = snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              description: data.description || data.task || '',
              assignedToUserId: data.assignedToUserId || data.assigneeId,
              linkedIssueId: data.linkedIssueId || data.issueId,
              dueDate: data.dueDate?.toDate?.() || data.dueDate,
              completedDate: data.completedDate?.toDate?.() || data.completedDate,
              createdAt: data.createdAt || Timestamp.now(),
              updatedAt: data.updatedAt || Timestamp.now(),
            } as ActionItem;
          });
          
          // Combine both sets and remove duplicates
          const combined = [...rockSpecificData, ...linkedData];
          setActionItems(Array.from(new Map(combined.map(item => [item.id, item])).values()));
          setLoading(false);
        },
        (error) => {
          console.error('Error loading rock-specific action items:', error);
          setLoading(false);
        }
      );

      const unsubLinked = onSnapshot(
        linkedQuery,
        (snapshot) => {
          linkedData = snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              description: data.description || data.task || '',
              assignedToUserId: data.assignedToUserId || data.assigneeId,
              linkedIssueId: data.linkedIssueId || data.issueId,
              dueDate: data.dueDate?.toDate?.() || data.dueDate,
              completedDate: data.completedDate?.toDate?.() || data.completedDate,
              createdAt: data.createdAt || Timestamp.now(),
              updatedAt: data.updatedAt || Timestamp.now(),
            } as ActionItem;
          });
          
          // Combine both sets and remove duplicates
          const combined = [...rockSpecificData, ...linkedData];
          setActionItems(Array.from(new Map(combined.map(item => [item.id, item])).values()));
          setLoading(false);
        },
        (error) => {
          console.error('Error loading linked action items:', error);
          setLoading(false);
        }
      );

      return () => {
        unsubRockSpecific();
        unsubLinked();
      };
    } else if (userId) {
      // General action items: load all for user (not dropped)
      const actionItemsQuery = query(
        collection(db, COLLECTION_NAMES.ACTION_ITEMS),
        where('dropped', '==', false),
        orderBy('createdAt', 'desc')
      );

      const unsubscribe = onSnapshot(
        actionItemsQuery,
        (snapshot) => {
          const actionItemsData = snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              // Ensure description exists (use task as fallback for legacy data)
              description: data.description || data.task || '',
              assignedToUserId: data.assignedToUserId || data.assigneeId,
              linkedIssueId: data.linkedIssueId || data.issueId,
              // Convert Firestore Timestamps to Date objects
              dueDate: data.dueDate?.toDate?.() || data.dueDate,
              completedDate: data.completedDate?.toDate?.() || data.completedDate,
              createdAt: data.createdAt || Timestamp.now(),
              updatedAt: data.updatedAt || Timestamp.now(),
            } as ActionItem;
          });
          setActionItems(actionItemsData);
          setLoading(false);
        },
        (error) => {
          console.error('Error loading action items:', error);
          setLoading(false);
        }
      );

      return () => unsubscribe();
    } else {
      // No userId or rockId provided
      setActionItems([]);
      setLoading(false);
    }
  }, [userId, rockId]);

  // Load Rocks, Issues, and Metrics for linking (only if userId provided, not for rock-specific)
  useEffect(() => {
    if (!userId || rockId) {
      // Skip loading related data for rock-specific queries
      return;
    }

    // Helper to create listeners
    const createListener = (collectionName: string, setter: Function) => {
      const q = query(collection(db, collectionName));
      return onSnapshot(q, (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setter(data);
      });
    };

    // Create listeners for all linkable items
    const unsubRocks = createListener(COLLECTION_NAMES.ROCKS, setRocks);
    const unsubIssues = createListener(COLLECTION_NAMES.ISSUES, setIssues);
    const unsubMetrics = createListener(COLLECTION_NAMES.METRICS, setRawMetrics);
    const unsubMetricNames = createListener(COLLECTION_NAMES.METRIC_NAMES, setMetricNames);

    return () => {
      unsubRocks();
      unsubIssues();
      unsubMetrics();
      unsubMetricNames();
    };
  }, [userId, rockId]);

  // Join metrics with metric names for display
  const metrics = useMemo(() => {
    if (rawMetrics.length === 0 || metricNames.length === 0) {
      return rawMetrics; // Return raw if names aren't loaded yet
    }

    const metricNamesMap = new Map(metricNames.map(m => [m.id, m.name]));

    const metricsWithNames = rawMetrics.map(metric => ({
      ...metric,
      // Join the name string for the UI
      metricName: metricNamesMap.get(metric.metricNameId) || 'Unknown Metric',
    }));

    return metricsWithNames;
  }, [rawMetrics, metricNames]);

  const createActionItem = async (formData: {
    description: string;
    dueDate: string;
    ownerId?: string;
    assignedToUserId?: string;
    linkedRockId?: string;
    linkedIssueId?: string;
    linkedMetricId?: string;
    rockId?: string; // For rock-specific action items
  }) => {
    if (!userId && !rockId) return;

    try {
      const now = Timestamp.now();
      const actionItemData: any = {
        description: formData.description.trim(),
        dueDate: new Date(formData.dueDate),
        complete: false,
        dropped: false,
        createdAt: now,
        updatedAt: now,
      };

      // Set rockId if provided (rock-specific) or linkedRockId (general linked to rock)
      if (formData.rockId) {
        actionItemData.rockId = formData.rockId;
        actionItemData.task = formData.description.trim(); // Legacy field
      } else if (formData.linkedRockId) {
        actionItemData.linkedRockId = formData.linkedRockId;
      }

      // Set owner and assignee
      if (formData.ownerId || userId) {
        actionItemData.ownerId = formData.ownerId || userId;
      }
      if (formData.assignedToUserId) {
        actionItemData.assignedToUserId = formData.assignedToUserId;
      }

      // Set links
      if (formData.linkedIssueId) {
        actionItemData.linkedIssueId = formData.linkedIssueId;
      }
      if (formData.linkedMetricId) {
        actionItemData.linkedMetricId = formData.linkedMetricId;
      }

      // Set createdByUserId
      if (userId) {
        actionItemData.createdByUserId = userId;
      }

      await addDoc(collection(db, COLLECTION_NAMES.ACTION_ITEMS), actionItemData);
    } catch (error) {
      console.error('Error creating action item:', error);
      throw error;
    }
  };

  const updateActionItem = async (
    actionItemId: string,
    formData: Partial<{
      description: string;
      dueDate: string | Date;
      ownerId: string;
      assignedToUserId: string;
      linkedRockId: string | null;
      linkedIssueId: string | null;
      linkedMetricId: string | null;
      rockId?: string;
      task?: string; // Legacy field
      assigneeId?: string; // Legacy field
      issueId?: string; // Legacy field
      complete?: boolean;
    }>
  ) => {
    try {
      const actionItemRef = doc(db, COLLECTION_NAMES.ACTION_ITEMS, actionItemId);
      const updateData: any = {
        ...formData,
        updatedAt: Timestamp.now(),
      };

      // Convert dueDate to Date if it's a string
      if (updateData.dueDate && typeof updateData.dueDate === 'string') {
        updateData.dueDate = new Date(updateData.dueDate);
      }

      // Trim description if provided
      if (updateData.description) {
        updateData.description = updateData.description.trim();
        // Also update task for backward compatibility
        updateData.task = updateData.description;
      }

      // Map legacy fields
      if (updateData.assigneeId) {
        updateData.assignedToUserId = updateData.assigneeId;
      }
      if (updateData.issueId) {
        updateData.linkedIssueId = updateData.issueId;
      }

      await updateDoc(actionItemRef, updateData);
    } catch (error) {
      console.error('Error updating action item:', error);
      throw error;
    }
  };

  const completeActionItem = async (actionItemId: string) => {
    try {
      const actionItemRef = doc(db, COLLECTION_NAMES.ACTION_ITEMS, actionItemId);
      await updateDoc(actionItemRef, {
        complete: true,
        completedDate: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error completing action item:', error);
      throw error;
    }
  };

  const uncompleteActionItem = async (actionItemId: string) => {
    try {
      const actionItemRef = doc(db, COLLECTION_NAMES.ACTION_ITEMS, actionItemId);
      await updateDoc(actionItemRef, {
        complete: false,
        completedDate: null,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error uncompleting action item:', error);
      throw error;
    }
  };

  const dropActionItem = async (actionItemId: string, reason?: string) => {
    try {
      const actionItemRef = doc(db, COLLECTION_NAMES.ACTION_ITEMS, actionItemId);
      await updateDoc(actionItemRef, {
        dropped: true,
        droppedReason: reason || null,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error dropping action item:', error);
      throw error;
    }
  };

  const deleteActionItem = async (actionItemId: string) => {
    try {
      const actionItemRef = doc(db, COLLECTION_NAMES.ACTION_ITEMS, actionItemId);
      await deleteDoc(actionItemRef);
    } catch (error) {
      console.error('Error deleting action item:', error);
      throw error;
    }
  };

  // Alias for backward compatibility (rock-specific components may use addActionItem)
  const addActionItem = async (data: {
    rockId: string;
    description?: string;
    task?: string; // Legacy field, maps to description
    assigneeId?: string; // Legacy field, maps to assignedToUserId
    issueId?: string; // Legacy field, maps to linkedIssueId
    dueDate?: string;
  }): Promise<void> => {
    const description = data.description || data.task;
    if (!description || !description.trim()) {
      throw new Error('Description is required.');
    }

    try {
      const now = Timestamp.now();
      const dueDate = data.dueDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      await createActionItem({
        description: description.trim(),
        dueDate,
        rockId: data.rockId,
        assignedToUserId: data.assigneeId,
        linkedIssueId: data.issueId,
      });
    } catch (error) {
      console.error('Error adding action item:', error);
      throw error;
    }
  };

  // Filter action items by completion status
  const getFilteredActionItems = (filter: 'all' | 'complete' | 'incomplete') => {
    if (filter === 'all') return actionItems;
    if (filter === 'complete') return actionItems.filter((actionItem) => actionItem.complete);
    return actionItems.filter((actionItem) => !actionItem.complete);
  };

  return {
    actionItems,
    users: eosUsers,
    rocks,
    issues,
    metrics,
    loading,
    createActionItem,
    addActionItem, // Alias for rock-specific usage
    updateActionItem,
    completeActionItem,
    uncompleteActionItem,
    dropActionItem,
    deleteActionItem,
    getFilteredActionItems,
  };
}
